// What happens to work already RUNNING: the task re-enqueue/fail/release/cancel hops,
// the run-event ingest, session compaction and its terminal states, the janitors, the
// provisioning and config-migration PRs they open, the plan lifecycle, error issues and
// the pull-request repairs. The housekeeping around it (retention, the gardener, the
// account / installation / repository rows the whole thing hangs off) continues in
// `./kind-housekeeping.ts`, which `./kind` spreads right after this group.
// Split out of `../audit.ts`, which is now the barrel over this directory. Not in
// the barrel: this is a HALF of `AuditEventKind`, exported only so `./kind` can
// assemble it. Import `AuditEventKind` — never a group.

export const AUDIT_KIND_MAINTENANCE = {
  // #117: the presence reaper reset a reaped runner's in-flight task back to
  // `pending` (same row, ownership cleared) so the assigner re-claims it for a
  // live box. Re-enqueueing lost work is a dispatch state change, so it earns
  // its own audit row — parallel to `TaskAssigned` on the way out. Audit is
  // trust: the trail must show the task moved, and why.
  TaskReenqueued: 'task.reenqueued',
  // Plan 101 runner lifecycle, doc 05 #7: the fleet event sink DROPPED a run event
  // it will never persist — a message it could not decode (`poison`), one it
  // terminated after `EVENT_MAX_DELIVERIES` identical failures (`terminated`), or
  // one whose subject the taxonomy does not produce (`subject_rejected`). Each was a
  // log line only, and a drop that leaves no row is indistinguishable from an event
  // that was never sent. Anchored on the ACCOUNT of the box that published it (the
  // subject proves the box; the box row names the tenant), never on the task's
  // session — the canonical `terminated` case is a task whose rows are gone.
  FleetEventDropped: 'fleet.event_dropped',
  // #4446: the box's own GC report — what the runner-restart hygiene pass found and did,
  // ONE row per box boot, landed by the box-event sink from
  // `fleet.box.<runnerId>.boot_hygiene`. Counts per finding kind and outcome, never a
  // path. Until it the pass wrote one journald line, so census M13 (stray process groups
  // at boot, plan 101 slice 16) had nothing to grade. In this group, not dispatch's with
  // the other `runner.*` rows: it is the box's janitor report, housekeeping like the
  // sink's drop row above.
  RunnerBootHygieneReported: 'runner.boot_hygiene_reported',
  // Plan 101 F7 (#4645): a BullMQ job stalled past `LANE_MAX_JOB_STALLS` (3) and the
  // worker REFUSED it rather than run it a fourth time — a job that keeps killing
  // its worker is not retried into the ground. Until this row the refusal was a
  // failed job that aged out of Redis in an hour and left nothing to count.
  // Platform-chain: the lane, not a tenant, owns it. Input:
  // `{ lane, jobId, stalledCounter, attemptsStarted, repeatJobKey | null }`.
  WorkerJobRefused: 'worker.job_refused',
  // Arc 101 WK-3: a task exhausted its `TASK_MAX_ATTEMPTS` retry budget (or hit
  // a non-retryable failure classification) and was declared TERMINALLY failed.
  // The retry lane's `TaskReenqueued` rows record each attempt going back to the
  // queue; this row is the one that says the platform STOPPED retrying, carrying
  // the failure kind + attempt count — without it a terminal `failed` row has no
  // trail naming why the work was abandoned.
  TaskFailed: 'task.failed',
  // plan 101 runner lifecycle, slice 01: a box GAVE a run back because the box itself
  // was going away (a SIGTERM, a self-update that outran the run ceiling, a lost fleet
  // connection). The task returns to `pending` at the SAME attempt with its fence
  // bumped. Distinct from `TaskReenqueued`, which records an attempt SPENT and re-pended
  // within the retry budget: a restart the task had nothing to do with must be
  // countable as what it is, and must never read as a failure in the trail.
  TaskReleased: 'task.released',
  // #6748 (epic #6552): a run PAUSED by a person stopped at a safe point, saved its work and
  // parked its task `paused` on the same machine, keeping its slot and its repo area. Its own
  // kind, never `TaskReleased`: the release streak bound (#5448) counts `task.released` rows,
  // and a task paused and resumed five times in an hour must never be ended
  // `box_release_loop`. Written by the box's own `run_released{paused}` and by every box
  // release that parks a pause-requested run (drain, lost box, operator drain or revoke).
  TaskPaused: 'task.paused',
  // #6751: the pause DOORS (`apps/api/src/deps/task-pause.ts`). A person asked a RUNNING job
  // to stop at its next step (`pause_requested`: `tasks.pause_requested_at` stamped and the
  // `pause` verb sent to its machine), withdrew that request before the box answered
  // (`pause_withdrawn`), or resumed a paused job (`resumed`: the row back to `pending`, its
  // machine pin kept or, with `anywhere`, cleared). A job not yet started parks at once and
  // writes `task.paused` above. Each row names the token and, when present, the user.
  TaskPauseRequested: 'task.pause_requested',
  TaskPauseWithdrawn: 'task.pause_withdrawn',
  TaskResumed: 'task.resumed',
  // #6768: one item of an accepted move SETTLED — the target's claim landed it (`landed`), or
  // it failed (`source_ended`, `target_released`, `cancelled`). Written in the settle's own
  // transaction: `{ task_id, batch_id, from, to, state, failure }`. A failed item's task is
  // left where the platform left it, never re-moved silently.
  TaskMoved: 'task.moved',
  // A PR closed WITHOUT merging, so its babysit state was retired: the watch
  // anchor plus any in-flight follow-up move `→ cancelled`. The mirror of
  // `TaskReenqueued` — that one records work being given BACK to the queue, this
  // one records work being taken OUT of it — and it earns a row for the same
  // reason: a maintainer whose queued fix silently vanished is owed the sentence
  // saying it was dropped because they closed the pull request. Only a call that
  // actually cancelled something writes one (the CAS is the gate), so a
  // redelivered `closed` webhook is silent.
  TaskCancelled: 'task.cancelled',
  // #4023: an OPERATOR re-drove a terminal (or otherwise stuck) task back to
  // `pending` through the sanctioned trigger (`POST /v1/tasks/:id/redispatch`)
  // — the only path back for a task whose ladder exhausted, since
  // `redispatchTask` (`@developerz/db`) revives a row's status but leaves
  // `attempt`/`failure_kind` exactly where they were: the ladder history IS
  // the audit, so this row is the operator's TRIGGER, not a ladder reset. Its
  // own kind rather than reusing `TaskReenqueued`: that one is the dispatch
  // lane's own automatic recovery (a reaped/cooled box), this one is a human
  // decision on a row the platform had already given up on.
  TaskRedispatched: 'task.redispatched',
  // #2270: an operator / control-plane verb that was filed as `mcp_tool.call.*` with its
  // name in `tool`. The value IS that name, so a new row's `kind` equals the old rows'
  // `tool`. For a name that was NEVER split, one query reads both:
  // `kind = $1 OR (kind LIKE 'mcp_tool.call.%' AND tool = $1)`. A name #6124 split by
  // verdict (an ok/err pair, or `plan.file_refused`) has its own reads, beside it.
  // The operator's cancel / redispatch REFUSED (`deps/mcp-ports.ts`, `plan-writes.ts`,
  // `task-redispatch.ts`, `review-rerun.ts`): the refusals are the rows an operator
  // goes looking for, beside the first-class `task.cancelled` / `task.redispatched`.
  TaskCancelRefused: 'task.cancel_refused',
  TaskRedispatchRefused: 'task.redispatch_refused',
  // An operator added `depends_on` edges to a task (`deps/task-operator.ts`, `box-claims.ts`).
  TaskDependenciesAdded: 'task.dependencies_added',
  // #5299: the `cron.dead_dependency` tick rewrote a pending task's `depends_on` because
  // it named a task a `plan regroup` SUPERSEDED — the edge now names the replacements
  // (`healSupersededDependencies`, `@developerz/db`). The regroup's own `plan.regrouped`
  // row is the decision; this is its follow-through on an edge written after it, and it
  // carries `before`/`after` so the rewrite can be undone.
  TaskDependenciesHealed: 'task.dependencies_healed',
  // #5299 follow-up: the `cron.dead_dependency` tick regrouped a plan's groups whose task
  // a `plan regroup` SUPERSEDED onto the replacements (`healSupersededPlanGroups`,
  // `@developerz/db`) — the case where a task is linked by a second plan (its ask's own)
  // the operator's regroup did not name. `input.regroups` lists each group replaced.
  PlanSupersededRegrouped: 'plan.superseded_regrouped',
  // 2026-09-28: the `cron.dead_dependency` tick removed `depends_on` edges that closed a
  // CYCLE among pending tasks — the edges pointing from an older task to a strictly newer
  // one, which is the edge a superseded-dependency heal wrote before it refused cycles
  // (`breakDependencyCycles`, `@developerz/db`). One row per dependent rewritten, with
  // `before`/`after`/`removed` and the whole `cycle`, so the removal can be undone.
  TaskDependencyCycleBroken: 'task.dependency_cycle_broken',
  // #366: the mothership wrote (or rewrote) a fleet task's lifecycle-narration
  // comment on the GitHub issue/PR it answers — "picked up by <box>", "PR
  // opened", "handed off". An OUTWARD write on the maintainer's repo, so it
  // earns a row on the same terms as `gh.issue_opened` and
  // `stacked_pr.links_written`. Only an APPLIED write writes one (an unchanged
  // body skips the PATCH entirely), so a redelivered stage event is silent.
  TaskNarrated: 'task.narrated',
  // #6082: the mothership DELETED a terminal task's saved branch — cancelled or merged at once,
  // failed once past `FAILED_SAVED_BRANCH_RETAIN_DAYS` (`savedBranchRetireEligibility`) — its own
  // `dz/<slug>--<8hex>` head or legacy `dz/wip/<task id>`, tip a saved commit, no PR on it. An
  // outward write on the maintainer's repo, so one row per delete performed
  // (`TaskSavedBranchRetiredOutputSchema`).
  TaskSavedBranchRetired: 'task.saved_branch_retired',
  // #908: a key-fix (`unverified|invalid → valid`) auto-replayed an entity whose
  // run had died on credentials — one row per re-driven entity + a sweep summary.
  // Audit is trust: an auto-replay of dropped work must leave a trail.
  StalledReplayRedriven: 'stalled_replay.redriven',
  // #118: the mothership event-sink ingested one `fleet.evt.<run_id>` event from
  // a runner and persisted it to `task_run_events` (the per-run hash chain that
  // powers the live terminal). `task_run_events` is cascade-deleted with its
  // task; this audit row is the durable, tamper-evident record that the
  // mothership accepted event X for run Y from an untrusted runner — audit is
  // trust, and it outlives the telemetry it attests to. Parent-less (the agent
  // loop lives on the BYOVM, so there is no mothership `agent_sessions` row).
  TaskRunEventIngested: 'task.run_event',
  // #129: in-session threshold compaction fired — the conversation prefix was
  // summarized (lossy) to keep a huge session inside the model's context
  // window. The row records how much was dropped so the trail shows the model
  // no longer sees the verbatim prefix.
  SessionCompacted: 'session.compacted',
  // arc 101 AC-8: compaction turned ITSELF OFF for the rest of the session —
  // the summarizer failed, so `session.compacted` will never fire again and the
  // context grows unsummarized until the model window refuses it. The absence
  // of further compaction rows is not evidence of a session that never needed
  // one, so the degradation earns a row of its own. Once per session.
  SessionCompactionDisabled: 'session.compaction_disabled',
  // The threshold tripped and compaction had nothing it was ALLOWED to drop:
  // `compactMessages` keeps the last steps verbatim, so a single tool result
  // larger than the window shrinks to nothing and the step goes to the provider
  // at full size. Nothing failed and nothing switched off — distinct from
  // `session.compaction_disabled` on both counts — but without a row of its own
  // the outcome is byte-identical to a session that never needed compacting:
  // prod `run_bf67826b39eb46c49b50b6ca8d0cba0f` reached 4.3x its declared window
  // with zero rows of either kind. One row per offending step, never latched:
  // the next step can genuinely compact once the history outgrows the verbatim
  // tail, and a once-only report would claim a situation resolved that is still
  // burning the window.
  SessionCompactionIneffective: 'session.compaction_ineffective',
  SessionSoftWarn: 'session.cost_soft_warn',
  SessionCompleted: 'session.completed',
  SessionEscalated: 'session.escalated',
  SessionFailed: 'session.failed',
  // 02-3: the terminal of a session the deploy drain cut — its job is handed back for
  // a fresh session. The `handed_back` outcome's row; a roll is not a failure.
  SessionHandedBack: 'session.handed_back',
  // #293: the janitor-cron framework. `janitor.run` brackets one bounded pass of
  // a scheduled maintenance agent — the start row carries the brief (prompt) +
  // scope, the end row the swept counts. `janitor.action` lands once per
  // applied / refused idempotent action (flag-stale, relabel, file-bug,
  // fleet-report). Parent-less jobs (no repo-scoped `agent_sessions` row), so —
  // like the other cron lanes — they are emitted through the worker's structured
  // job-audit log. Never a PR: the janitor write vocabulary has no PR member
  // (see `apps/worker/src/janitors`), so "open a PR" is unrepresentable.
  JanitorRun: 'janitor.run',
  JanitorAction: 'janitor.action',
  // A `janitor.action` whose finding's tenant resolved to NO account (the repo or box
  // row is gone, or the lookup faulted). The action already ran, so it is not
  // dropped and never guessed onto another tenant: it lands on the janitor's
  // PLATFORM chain, the pass's own head, with the action row's input/output plus
  // `tenant` and `reason` (`no_account` | `lookup_failed`). Platform-owned by
  // construction — the one janitor action kind with no account.
  JanitorActionUnattributed: 'janitor.action_unattributed',
  // #447: the onboarding `.mcp.json` provisioner. `mcp_provision.checked` lands
  // once per installed repo the worker inspected (decision: created / merged /
  // unchanged / malformed / skipped-and-why); `mcp_provision.pr_opened` once per
  // disclosed bot PR it opened to wire the managed MCP servers. Two kinds so the
  // trail separates the (idempotent, frequent) check from the (rare) write —
  // audit is trust: every check and every PR action must be on the record.
  McpProvisionChecked: 'mcp_provision.checked',
  McpProvisionPrOpened: 'mcp_provision.pr_opened',
  // #2963: the `.dz/` migration consumer — the half that OPENS the per-repo
  // move PRs (the guard + inventory it composes are #2961). Mirrors the
  // `mcp_provision.*` pair for the same reason: `dz_config_migrate.checked`
  // lands once per installed repo the worker inspected (decision:
  // `pr_opened` / `dry_run` / `unchanged` / `skipped-and-why`); the second
  // row lands ONCE per disclosed bot PR — the (rare) write — and never
  // outside a DRAFT. Two kinds so the trail separates the (idempotent,
  // frequent) check from the (rare) write; audit is trust — every check and
  // every PR action must be on the record. Parent-less (a queue lane, no
  // `agent_sessions` row), so they ride the worker's structured job-audit
  // store beside the provisioner rows above.
  DzConfigMigrateChecked: 'dz_config_migrate.checked',
  DzConfigMigratePrOpened: 'dz_config_migrate.pr_opened',
  // #3149: the `.dz/` migration READY lane — the half that re-runs the #2961
  // guard on a still-open DRAFT move PR and marks it ready for review only
  // when the guard still passes (`apps/worker/src/consumers/
  // dz-config-migrate-ready.ts`). The lane REUSES `dz_config_migrate.checked`
  // for every per-repo check it performs (one vocabulary for "the worker
  // looked at this repo's migration state"), so this kind lands ONCE per PR
  // actually readied — the (rare) write, beside `pr_opened` above. The lane
  // never merges and never arms auto-merge: on developerz.ai the platform App
  // merges a ready PR on green, so readying IS the merge trigger — which is
  // why the guard re-runs at this moment and not only at open time.
  DzConfigMigrateReadied: 'dz_config_migrate.readied',
  // #511 task 3: the maintainer bot edited a fleet slice PR's body to add its
  // stacked-plan links (`Part n/m of plan <id>` + `Depends on #<upstream PR>`).
  StackedPrLinksWritten: 'stacked_pr.links_written',
  // #990: a plan group flipped `→ merged` because the PR its task opened was
  // reported merged by GitHub — the lifecycle transition that unblocks the
  // plan DAG's downstream slices in `claimNextReady`. Parent-less (fired from
  // the worker's PR-projection sink), so it is emitted through the worker's
  // structured job-audit log.
  PlanGroupMerged: 'plan_group.merged',
  // #3800: a plan's group DAG was WRITTEN — the batch `insertPlanGroups` /
  // `insertPlanGroupsIfNewPlan` (`packages/db` `plan-groups/write.ts`) persisted, held
  // or not. Appended through their REQUIRED `record` INSIDE the write's transaction, so
  // the row exists iff the groups do; a fully redelivered batch writes none. `input`
  // names each group's id, repo, edges and status, never an envelope or a title (the
  // one draft: `@developerz/audit` `planRecordedDraft`). Writers, every one on the
  // account's chain through `appendTenantAuditOnTx`: `apps/worker`
  // `wiring/plan-recorded.ts` (the scout ingest, the onboarding scaffold and backfill,
  // `plan execute`), `apps/api` `deps/plan-recorded.ts` (the project create/setup
  // scaffold) and `apps/agents-manager` `wiring/plan-recorded.ts` (the triage lane).
  PlanRecorded: 'plan.recorded',
  // #4018: the plan itself flipped `→ done` because its LAST group merged — the
  // stored, system-written completion of an epic. `output.source` names which of
  // the two writers did it: `pr_merged` (the group flip's own transaction, from
  // the PR-projection sink) or `reconcile` (the `cron.dead_dependency` tick
  // closing a plan that was already all-merged before the writer existed).
  // DURABLE on the account's chain, unlike `plan_group.merged` beside it: the
  // whole point of storing completion is that a query can answer it later.
  PlanDone: 'plan.done',
  // Plan 101 slice 06: a `success` deploy made an upstream leg LIVE where a `deployed` edge
  // waits; one row per leg per release (a repeat success writes none), in the verdict insert's
  // transaction. `input`: `{ planId, groupId, repoFullName, environment, sha, deliveryId }`.
  PlanLegDeployed: 'plan.leg_deployed',
  // Plan 101 slice 07: a leg released its `hold: merge` dependents (`leg-release-wake.ts`).
  PlanLegReleased: 'plan.leg_released',
  // Plan 101 slice 09: a live check's verdict and the shas it proved at settle (`live-verdict-settle.ts`).
  PlanLiveVerdict: 'plan.live_verdict',
  // Plan 101 R3: the plan flipped `→ ended` — every slice settled (failed, cancelled or
  // merged, none superseded) and not every slice landed. Written by the
  // `cron.dead_dependency` tick (`reconcilePlansEnded`); DURABLE on the account's chain
  // beside `plan.done`, for the same reason.
  PlanEnded: 'plan.ended',
  // Plan 101 R3: an `ended` plan went back to `open` because a redispatch revived one of
  // its slices (`reopenRevivedPlans`, in the redispatch's own transaction). Written by
  // the redispatching caller on the account's chain, beside its `task.redispatched` row.
  PlanReopened: 'plan.reopened',
  // Plan 101 D7 (slice 10 PR 8b): the repair filer (`cron.dead_dependency`) filed one repair
  // ask for an ended plan — or, past `PLAN_REPAIR_ATTEMPTS_MAX`, filed it pre-settled
  // `escalated`. `input` is `PlanRepairFiledInputSchema` (`value-objects/plan-repair.ts`).
  // DURABLE on the account's chain beside `plan.ended`.
  PlanRepairFiled: 'plan.repair_filed',
  // Plan 101 D7 (slice 10 PR 8b): triage appended repair legs to an existing plan and
  // discharged the failed legs they replace (`appendPlanRepairLegs`, `@developerz/db`), in
  // that append's own transaction through its REQUIRED `record`. `input` is
  // `PlanLegsAppendedInputSchema` (`value-objects/plan-repair.ts`), naming each `supersedes`.
  PlanLegsAppended: 'plan.legs_appended',
  // #2270 (the note above `TaskCancelRefused`): promoted from `mcp_tool.call.*`.
  // The operator's plan verbs (`deps/plan-writes.ts`): approve (and its refusal / no-op),
  // regroup, unlink, cancel.
  PlanApproved: 'plan.approved',
  PlanApproveRefused: 'plan.approve_refused',
  PlanApprovedNoop: 'plan.approved_noop',
  PlanRegrouped: 'plan.regrouped',
  PlanRegroupRefused: 'plan.regroup_refused',
  PlanUnlinkRefused: 'plan.unlink_refused',
  PlanEdgesUnlinked: 'plan.edges_unlinked',
  PlanCancelled: 'plan.cancelled',
  // #5131: the `plan.mirror` lane filed or updated a GitHub mirror issue on a
  // customer repo — the durable half of the platform's plan-as-issues loop
  // (#5100). `input`/`output` carries `{ planId, part, repoFullName, issueNumber,
  // action }`; `action` is one of `reused | opened | updated | closed` across
  // #5132 (create) and #5133 (keep current). No producer writes this row in
  // #5131: the kind and its `TERMINAL_RENDERERS` entry ship here so the lane
  // can mount the audit arm in #5132 without the project's `kind` enum
  // shifting under any earlier merger.
  PlanMirrored: 'plan.mirrored',
  // #5653: the error-issue lane (`apps/worker/src/consumers/error-issue.ts`)
  // filed one GitHub issue for an error group, beside the `error_issues` ledger
  // row. `input` carries `{ repoFullName, groupKey, rawTaskId }` and `output`
  // carries `{ issueNumber, url }`. The writers are the POST branch in
  // `runClaimedErrorIssuePass` (`error-issue.ts`: the issue it posted, audited
  // whether or not its ledger insert raced) and `ledgerRefoundIssue`
  // (`error-issue-ledger.ts`: an issue a crashed pass left on the repo,
  // audited when its ledger insert lands the row). Rendered 'omit' in
  // TERMINAL_RENDERERS (maintenance-tenancy.ts), as `plan.mirrored` is: the
  // maintainer sees the GitHub issue itself.
  ErrorIssueFiled: 'error_issue.filed',
  // #5653: the same lane CAPPED a group: the per-repo daily issue budget was
  // already spent, so no issue was filed and the `capped` ledger row is the
  // durable record of the decision. `input` carries
  // `{ repoFullName, groupKey, rawTaskId, day }`, `day` being the UTC day the
  // cap counted, and `output` is null. Written by `settleCappedGroup`
  // (`error-issue-ledger.ts`), only when its `capped` ledger insert lands the
  // row. Also written, with `input.reason` (`original_capped`, `original_refused`,
  // `original_unpublished`), by `settleUnpublishedOriginal` (`error-issue-reopen.ts`,
  // #5654) for a recurrence whose original was never published; `reason` stays in
  // `input` as stored since #6398 (#6403). Same lane as `error_issue.filed` beside it,
  // and rendered 'omit' in TERMINAL_RENDERERS likewise.
  ErrorIssueCapped: 'error_issue.capped',
  // #5654: the same lane REOPENED the original public issue of a group whose
  // merged fix was followed by a recurrence (a generation >= 1 candidate), with one
  // disclosed comment naming the fix PR and the generation, instead of filing a new
  // issue. `input` carries `{ repoFullName, groupKey, baseGroupKey, generation,
  // rawTaskId }` and `output` carries `{ issueNumber, url, fixPullRequest }`
  // (`fixPullRequest` is null when the merged task recorded no PR). Written by the
  // reopen pass (`error-issue-reopen.ts`) only when its ledger insert landed the
  // row. Rendered 'omit' like its siblings: the maintainer sees the issue itself.
  ErrorIssueReopened: 'error_issue.reopened',
  // #5654: the reopen pass DECLINED to act on the original issue — it is gone (404 /
  // 410 on the read, the comments read, the comment POST or the state PATCH), moved to
  // another repository, a pull request, `locked` (read off the issue object, before any
  // comment), closed by a maintainer as not planned (or duplicate / with no reason). A 403
  // is NOT one of these: it is the REPO's (archived repos serve reads and refuse writes; a
  // missing issues:write permission answers alike) and is remembered, not recorded here.
  // Nothing public was changed (apart from a comment already posted: `commentPosted`, set
  // when the crash-recovery read found this generation's comment, or a later PATCH was
  // refused). `input` carries `{ repoFullName, groupKey,
  // baseGroupKey, generation, rawTaskId }`; `output` carries `{ reason }` and, for an
  // HTTP refusal, `{ status }`. The generation's ledger row is `capped`. Rendered
  // 'omit', as above.
  ErrorIssueReopenRefused: 'error_issue.reopen_refused',
  // #5657: the error-fix confirmation lane (`cron.error_fix_confirm`, `apps/worker/src/cron/
  // error-fix-confirm.ts`) posted ONE disclosed comment on a group's already-closed public
  // issue: a successful deployment of the fix was reported and no recurrence was recorded for N
  // hours (a success verdict is a report, not proof production changed). It confirms and never
  // closes. `input` carries `{ repoFullName, groupKey, baseGroupKey, generation,
  // rawTaskId }` and `output` carries `{ issueNumber, url, fixPullRequests, deployedAt,
  // windowHours }`. Written only when the `error_issue_confirmations` row THIS write landed
  // (a lost insert race writes no second audit row). Rendered 'omit': the maintainer sees the
  // GitHub comment itself.
  ErrorIssueFixConfirmed: 'error_issue.fix_confirmed',
  // #5657: the same lane DECLINED to confirm a generation. `input` as above; `output`
  // carries `{ reason }` and, for an HTTP refusal, `{ status }`; `commentPosted: true` when
  // the crash-recovery read found a comment of this generation already on the issue before a
  // state refusal. Reasons: `unrecognised_key`, `recurred`, `issue_open`, `locked`, `pull_request`,
  // `repository_mismatch`, `not_planned`, `duplicate`, `no_state_reason`, `issue_gone`.
  // The generation's `error_issue_confirmations` row is `declined`. Rendered 'omit'.
  ErrorIssueFixConfirmRefused: 'error_issue.fix_confirm_refused',
  // `cron.error_issue_settle`: the lane closed a group's public issue because every task of the
  // generation settled with no merged fix and no open pull request. Input `{ repoFullName,
  // groupKey, baseGroupKey, generation, rawTaskId }`, output `{ issueNumber, url, outcome }`.
  // Written only when the `error_issue_settlements` row THIS write landed.
  ErrorIssueSettledClosed: 'error_issue.settled_closed',
  // The same lane decided it will never close this generation's issue, with the reason. Same
  // input; output `{ reason }`. The `error_issue_settlements` row is `declined`.
  ErrorIssueSettleDeclined: 'error_issue.settle_declined',
  // #5658: the error-fix RELEASE lane (`cron.error_fix_release`, `apps/worker/src/cron/
  // error-fix-release.ts`) posted ONE disclosed "Shipped in <tag>" comment on a group's public
  // issue: the first stable GitHub release that contains every merged fix of the generation. It
  // announces and never closes, reopens, edits or labels. `input` carries `{ repoFullName,
  // groupKey, baseGroupKey, generation, rawTaskId }` and `output` carries `{ issueNumber, url,
  // fixPullRequests, releaseTag, releasePublishedAt, staleLiveTasks }` (`staleLiveTasks`: how many
  // otherwise-live tasks of the generation had no status change in 14 days and so did not hold
  // the announcement back). Written BEFORE the `error_issue_release_notes` row, so a process that
  // dies between the two leaves no row and the next tick audits again (an accepted duplicate); a
  // pass that loses the insert race has already written its audit, and the `error_fix_release_race`
  // log names the winner. Rendered 'omit': the maintainer sees the GitHub comment itself.
  ErrorIssueFixReleased: 'error_issue.fix_released',
  // #5658: the same lane DECLINED to announce a release on a generation's issue. `input` as
  // above; `output` carries `{ reason }` and, for an HTTP refusal, `{ status }`;
  // `commentPosted: true` when the crash-recovery read found a comment of this generation
  // already on the issue before a state refusal. Reasons: `locked`, `pull_request`,
  // `repository_mismatch`, `not_planned`, `duplicate`, `no_state_reason`, `issue_gone`,
  // `compare_unresolvable` (a fix sha 404ed against only SOME compares of one aged release over
  // repeated sightings: that release is not proven to contain every fix, not proven to lack one).
  // A `compare_unresolvable` refusal is a FORCED decline and carries six MORE fields beside the
  // reason: `{ repoFullName, groupKey, tag, notFound, shaCount, firstSeenAt }` — the release that
  // stayed unresolved, how many of its fix shas 404ed, and when the FIRST partial-404 sighting
  // was (the instant the decline is measured from). The generation's `error_issue_release_notes`
  // row is `declined`. Written BEFORE that row, like
  // `ErrorIssueFixReleased`: a pass that loses the insert race has already audited its refusal.
  // Rendered 'omit'.
  ErrorIssueFixReleaseRefused: 'error_issue.fix_release_refused',
  // #5659: `cron.error_fix_timing` posted ONE timing-only comment on a NEW error's own issue (first
  // seen shortly after a fix's reported deploy). 'omit': the maintainer sees the comment itself.
  ErrorIssueFixTimingNoted: 'error_issue.fix_timing_noted',
  // #5659: the same lane declined (reasons as `fix_release_refused`). Rendered 'omit'.
  ErrorIssueFixTimingRefused: 'error_issue.fix_timing_refused',
  // #1253: a SELF-MERGE was recorded — the PR's author and its merger are the
  // same GitHub login. NEUTRAL by design: the row presumes no verdict on
  // legitimacy — no `violation`, no `sanctioned`, no `allowed` field, no
  // severity, no escalation. It records; something else may later judge (the
  // sanction question stays on parent #1205). Fired from the worker's always-on
  // PR-projection sink on the one action that reports a merge (`closed` +
  // `merged: true`), which covers babysit auto-merge AND a human merge.
  // Parent-less, so it rides the worker's structured job-audit store like
  // `plan_group.merged` beside it.
  PrSelfMerged: 'pr.self_merged',
  // #1787: a HUMAN-authored PR was linked to the issue it answers — the durable
  // half of the reporter-facing loop, whose accept path ("open a PR whenever you
  // are ready") previously ended at a sentence nothing followed. `output` carries
  // `{repo_full_name, pr_number, issue_number, source}`, where `source` names
  // which of the two link readings found it (`graphql` = GitHub's own
  // `closingIssuesReferences`, `body_keyword` = the body's closing keywords).
  // NOT parent-less: a human-authored PR mints no agent session, so the row is
  // anchored on a gateway-style account parent (`ensureGatewaySessionParent`, the
  // #823/#973 pattern) and lands in `audit_events` rather than the stdout job
  // log — the link is the deliverable here, so it has to be queryable.
  PrIssueLinked: 'pr.issue_linked',
  // The babysit split follow-through (`apps/worker/src/babysit/split-followthrough.ts`)
  // made an OUTWARD write on OUR bot's non-converging PR once triage cut its split:
  // `output.action` is `closed` (closed + the marker comment posted), `comment_posted`
  // (a close whose comment had not landed) or `comment_updated` (the marker comment
  // edited to link a split PR that opened). Only an APPLIED write writes one. The
  // supersede itself — cancel + regroup — is its own `task.cancelled` row, written
  // before any GitHub call.
  PrSplitRetired: 'pr.split_retired',
  // #2270 (the note above `TaskCancelRefused`): promoted from `mcp_tool.call.*`.
  // An operator re-armed a PR's babysit (`deps/pr-babysit-rearm.ts`). Its sibling
  // `pr.pending_decided` is NOT here: it is a stdout belt line beside the durable
  // `tool.call.ok` / `action.redeem` row (`deps/pr-decide-pending.ts`), never an
  // account-audit row, so there was nothing to promote.
  PrBabysitRearmed: 'pr.babysit_rearmed',
  /**
   * #3702/#4283: `scripts/backfill-merge-commit-sha.ts --confirm` overwrote a
   * `pull_requests.merge_commit_sha` that held GitHub's ephemeral mergeability
   * PROBE (written before #4283 gated the read on the merged payload) with the
   * real merge commit GitHub now reports for that pull request. `input` carries
   * `{repoId, githubNumber}`; `output` carries `{stored, real}` — the probe and
   * the commit written in its place. One row per repaired candidate, appended in
   * the SAME transaction as the `pull_requests` UPDATE: a failing append rolls
   * the write back with it, so a repaired row is never unlogged. On the chain of
   * the account that owns the repo (deep sweep 01-15).
   */
  PrMergeCommitShaRepaired: 'pr.merge_commit_sha_repaired',
  /**
   * #3935: `scripts/backfill-pr-merged-at.ts --confirm` filled the merge timestamps
   * GitHub reports onto a `merged` `pull_requests` row written before #3901 clocked
   * them (`merged_at` was NULL). `input` carries `{repoId, githubNumber}`; `output`
   * carries GitHub's `{mergedAt, closedAt, openedAt, mergeCommitSha}` as read. Every
   * column but `merged_at` was filled only where it was NULL. One row per filled
   * candidate, appended in the SAME transaction as the UPDATE, so a filled row is
   * never unlogged. On the chain of the account that owns the repo (deep sweep 01-15).
   */
  PrMergeTimestampsBackfilled: 'pr.merge_timestamps_backfilled',
  // Plan 101 rows 07-2 / 09-1: the PR-projection sink REFUSED to link a pull request to
  // the task its `dz-task` marker named. Nothing was written to the task, and a write
  // that did not happen leaves no other row, so the refusal is its own. `input` carries
  // `{accountId, repo, prNumber, taskId}`; `output` carries `{linked: false, reason}`
  // plus what that refusal knows. WHICH refusals exist, and what each adds to `output`,
  // is the writer's to enumerate and is not restated here: `reason` is a member of
  // `MarkerLinkRefusal['kind']` (`apps/worker/src/babysit/marker-link.ts`), and the row
  // is built by `refusedMarkerLinkAuditEvent` (`apps/worker/src/wiring/
  // pr-projection-sink.ts`). Visible to the maintainer: a pull request claimed one of
  // their tasks.
  TaskPrLinkRefused: 'task.pr_link_refused',
} as const;
